import React, { useState } from "react";
import { Shield, Phone } from "lucide-react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";

export const SOSButton = () => {
  const [open, setOpen] = useState(false);
  const [signaling, setSignaling] = useState(false);
  const [signal, setSignal] = useState(null);

  const trigger = async () => {
    setSignaling(true);
    let geo = {};
    try {
      // Best-effort coarse location (resource selection only — never tied to a person on dashboard)
      const pos = await new Promise((res, rej) => {
        if (!navigator.geolocation) return rej();
        navigator.geolocation.getCurrentPosition(res, rej, { timeout: 3000 });
      });
      geo = { lat: pos.coords.latitude, lng: pos.coords.longitude };
    } catch (e) { /* ignore — geo optional */ }

    try {
      const r = await api.post("/sos/trigger", { geo_resources: geo });
      setSignal(r.data);
    } catch (e) {
      toast.error("Unable to start SOS session.");
    } finally {
      setSignaling(false);
    }
  };

  return (
    <>
      <button
        data-testid="sos-trigger-button"
        onClick={() => { setOpen(true); setSignal(null); }}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold uppercase tracking-widest text-white shadow-lg transition-opacity hover:opacity-90"
        style={{ background: "hsl(var(--haruki-teal))" }}
      >
        <Shield className="h-4 w-4" />
        SOS
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="sos-modal" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Confidential SOS Session</DialogTitle>
            <DialogDescription>
              Your agency receives a silent signal — never the content of what you say or write.
              This session is routed to WYSA. EAP referral is handled by WYSA.
            </DialogDescription>
          </DialogHeader>

          {!signal ? (
            <div className="space-y-4 py-3">
              <div className="rounded-md border p-4 text-sm leading-relaxed">
                Pressing <strong>Start session</strong> sends a silent signal containing only the
                timestamp and the geographic resources activated. It does <strong>not</strong> send any
                of your conversation or any information about your state of mind.
              </div>
              <Button
                data-testid="sos-start-session-button"
                onClick={trigger}
                disabled={signaling}
                className="w-full"
                style={{ background: "hsl(var(--haruki-teal))", color: "white" }}
              >
                <Phone className="mr-2 h-4 w-4" />
                {signaling ? "Starting session…" : "Start session"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4 py-3">
              <div className="rounded-md border p-4 text-sm">
                {signal.message}
              </div>
              <iframe
                data-testid="sos-wysa-iframe"
                title="WYSA"
                src={signal.wysa_iframe_url}
                className="h-80 w-full rounded-md border"
              />
              <p className="text-xs text-muted-foreground">
                Placeholder embed — full WYSA integration enables in production.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SOSButton;
